import type { 
  OrchestratorMessage, 
  MessageTypesType,
  MessageSourcesType,
  MessageHandler, 
  QueuedMessage,
  ResponseMessage,
  DataMessage
} from '../types';
import { MessageTypes } from '../types';

import { 
  validateMessage, 
  createBaseMessage, 
  createDataMessage, 
  createErrorMessage,
  generateRequestId,
  logMessage,
  isOriginAllowed,
  hasRequestId,
  transformEventBridgeMessage
} from '../utils';

import {
  DEFAULT_MESSAGE_TIMEOUT,
  DEFAULT_MAX_RETRIES,
  MESSAGE_DEDUPE_WINDOW,
  MESSAGE_CLEANUP_THRESHOLD,
  MESSAGE_CLEANUP_CUTOFF_MULTIPLIER
} from '../constants';
import { createLogger } from '../utils';

/**
 * Configuration for MessageBridge
 */
export interface MessageBridgeConfig {
  allowedOrigins?: string[];
  debug?: boolean;
  timeout?: number;
  maxRetries?: number;
}

/**
 * MessageBridge handles cross-origin communication between parent and iframe
 * 
 * This class provides a reliable messaging system with features like:
 * - Message deduplication to prevent processing the same message multiple times
 * - Request/response correlation with timeout handling
 * - Automatic cleanup of old messages to prevent memory leaks
 * - Debug logging for troubleshooting
 * 
 * @example
 * ```typescript
 * const bridge = new MessageBridge('orchestrator', { debug: true });
 * bridge.setTargetWindow(iframe.contentWindow);
 * 
 * bridge.on('ready', (message) => {
 *   console.log('Iframe is ready');
 * });
 * 
 * bridge.send('config-update', { theme: 'dark' });
 * ```
 */
export class MessageBridge {
  private messageHandlers = new Map<MessageTypesType, Set<MessageHandler>>();
  private pendingRequests = new Map<string, QueuedMessage>();
  private eventListeners: (() => void)[] = [];
  private config: Required<MessageBridgeConfig>;
  private source: MessageSourcesType;
  private targetWindow: Window | null = null;
  private isDestroyed = false;
  private processedMessages = new Set<string>();
  private readonly messageDedupeWindow = MESSAGE_DEDUPE_WINDOW;
  private messageHandlingInProgress = new Set<string>(); // Track messages currently being processed
  private logger = createLogger('MessageBridge');
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(
    source: MessageSourcesType,
    config: MessageBridgeConfig = {}
  ) {
    this.source = source;
    this.config = {
      allowedOrigins: [],
      debug: false,
      timeout: DEFAULT_MESSAGE_TIMEOUT,
      maxRetries: DEFAULT_MAX_RETRIES,
      ...config
    };

    // Configure logger debug level
    this.logger.setLevel(this.config.debug ? 3 : 1); // DEBUG : WARN

    this.setupMessageListener();
    this.startPeriodicCleanup();
  }

  /**
   * Set target window for sending messages
   */
  setTargetWindow(window: Window | null): void {
    this.targetWindow = window;
  }

  /**
   * Add message handler for specific message type
   */
  on<T extends OrchestratorMessage = OrchestratorMessage>(
    type: MessageTypesType, 
    handler: MessageHandler<T>
  ): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    
    this.messageHandlers.get(type)!.add(handler as MessageHandler);
    
    // Return cleanup function
    return () => this.off(type, handler);
  }

  /**
   * Remove message handler
   */
  off<T extends OrchestratorMessage = OrchestratorMessage>(
    type: MessageTypesType, 
    handler?: MessageHandler<T>
  ): void {
    const handlers = this.messageHandlers.get(type);
    if (!handlers) return;

    if (handler) {
      handlers.delete(handler as MessageHandler);
    } else {
      handlers.clear();
    }
  }

  /**
   * Send message without expecting response
   */
  send(type: MessageTypesType, data?: unknown): void {
    if (this.isDestroyed) {
      this.logger.error('Cannot send message: bridge is destroyed');
      return;
    }

    const message = data !== undefined 
      ? createDataMessage(type, this.source, data)
      : createBaseMessage(type, this.source);

    this.postMessage(message);
  }

  /**
   * Send message and wait for response
   */
  async request<T = unknown>(type: MessageTypesType, data?: unknown): Promise<T> {
    if (this.isDestroyed) {
      throw new Error('Cannot send request: bridge is destroyed');
    }

    return new Promise((resolve, reject) => {
      const requestId = generateRequestId();
      const message = data !== undefined
        ? createDataMessage(type, this.source, data, requestId)
        : { ...createBaseMessage(type, this.source), requestId };

      // Store pending request
      const queuedMessage: QueuedMessage = {
        message,
        resolve,
        reject,
        timeout: Date.now() + this.config.timeout,
        timestamp: Date.now()
      };

      this.pendingRequests.set(requestId, queuedMessage);

      // Set timeout
      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          reject(new Error(`Request timeout for ${type} (${this.config.timeout}ms)`));
        }
      }, this.config.timeout);

      // Send message
      this.postMessage(message);
    });
  }

  /**
   * Send response to a received request - DEPRECATED
   * API calls are no longer supported via message bridge
   */
  // respond<T = any>(originalMessage: OrchestratorMessage, success: boolean, data?: T, error?: string): void {
  //   console.warn('[MessageBridge] respond() is deprecated - API calls no longer supported');
  // }

  /**
   * Send error response - DEPRECATED
   * API calls are no longer supported via message bridge
   */
  // respondWithError(originalMessage: OrchestratorMessage, error: string | Error): void {
  //   console.warn('[MessageBridge] respondWithError() is deprecated - API calls no longer supported');
  // }

  /**
   * Emit event to all handlers
   */
  emit(type: MessageTypesType, data?: unknown): void {
    const message = data !== undefined
      ? createDataMessage(type, this.source, data)
      : createBaseMessage(type, this.source);

    this.handleMessage(message);
  }

  /**
   * Cleanup and destroy bridge
   */
  destroy(): void {
    if (this.isDestroyed) return;

    this.isDestroyed = true;
    this.logger.debug('Destroying MessageBridge');

    // Stop periodic cleanup
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // Clean up event listeners
    this.eventListeners.forEach(cleanup => cleanup());
    this.eventListeners.length = 0;

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('MessageBridge destroyed'));
    });
    this.pendingRequests.clear();

    // Clear message handlers
    this.messageHandlers.clear();

    // Clear processed messages
    this.processedMessages.clear();
    this.messageHandlingInProgress.clear();

    // Reset target window
    this.targetWindow = null;

    this.logger.debug('MessageBridge destroyed');
  }

  /**
   * Get statistics about the bridge
   */
  getStats(): {
    pendingRequests: number;
    messageHandlers: number;
    isDestroyed: boolean;
    source: MessageSourcesType;
  } {
    return {
      pendingRequests: this.pendingRequests.size,
      messageHandlers: Array.from(this.messageHandlers.values())
        .reduce((total, handlers) => total + handlers.size, 0),
      isDestroyed: this.isDestroyed,
      source: this.source
    };
  }

  /**
   * Setup window message listener
   */
  private setupMessageListener(): void {
    const handleWindowMessage = (event: MessageEvent) => {
      // Skip if bridge is destroyed
      if (this.isDestroyed) return;

      // Validate message
      const validation = validateMessage(event);
      if (!validation.isValid) {
        // Only log warning if not marked as silent (browser extensions)
        if (this.config.debug && !validation.silent) {
          console.warn('[MessageBridge] Invalid message:', validation.error, event.data);
        }
        return;
      }

      // Check origin restrictions
      if (!isOriginAllowed(event.origin, this.config.allowedOrigins)) {
        if (this.config.debug) {
          console.warn('[MessageBridge] Message from disallowed origin:', event.origin);
        }
        return;
      }

      // Transform EventBridge messages if needed
      let message: OrchestratorMessage;
      if (validation.isEventBridge) {
        const transformed = transformEventBridgeMessage(event.data);
        if (!transformed) {
          return; // Should not happen as validation passed
        }
        message = transformed;
      } else {
        message = event.data as OrchestratorMessage;
      }

      // Handle the message
      this.handleMessage(message);
    };

    window.addEventListener('message', handleWindowMessage);
    
    // Store cleanup function
    this.eventListeners.push(() => {
      window.removeEventListener('message', handleWindowMessage);
    });
  }

  /**
   * Generate message fingerprint for deduplication
   */
  private getMessageFingerprint(message: OrchestratorMessage): string {
    // Create a fingerprint from message type, source, and data
    const dataStr = message.type === MessageTypes.MESSAGE_SENT || 
                   message.type === MessageTypes.MESSAGE_RECEIVED
      ? JSON.stringify((message as DataMessage).data?.message?.message_id || '')
      : JSON.stringify((message as DataMessage).data || '');
    return `${message.type}-${message.source}-${dataStr}`;
  }

  /**
   * Handle incoming message
   */
  private async handleMessage(message: OrchestratorMessage): Promise<void> {
    // Check for duplicate messages
    const fingerprint = this.getMessageFingerprint(message);
    const fingerprintWithTime = `${fingerprint}-${Math.floor(message.timestamp / this.messageDedupeWindow)}`;
    
    if (this.processedMessages.has(fingerprintWithTime)) {
      if (this.config.debug) {
        console.warn('[MessageBridge] Duplicate message detected, skipping:', message.type);
      }
      return;
    }

    // Prevent re-entrant message processing (breaks circular loops)
    if (this.messageHandlingInProgress.has(fingerprint)) {
      if (this.config.debug) {
        console.warn('[MessageBridge] Message already being processed, preventing circular loop:', message.type);
      }
      return;
    }
    
    // Mark as being processed
    this.messageHandlingInProgress.add(fingerprint);
    
    try {
      // Mark as processed
      this.processedMessages.add(fingerprintWithTime);
    
    // Clean up old fingerprints periodically
    if (this.processedMessages.size > MESSAGE_CLEANUP_THRESHOLD) {
      const cutoff = Date.now() - (this.messageDedupeWindow * MESSAGE_CLEANUP_CUTOFF_MULTIPLIER);
      this.processedMessages.forEach(fp => {
        const time = parseInt(fp.split('-').pop() || '0') * this.messageDedupeWindow;
        if (time < cutoff) {
          this.processedMessages.delete(fp);
        }
      });
    }

    if (this.config.debug) {
      logMessage('received', message, true);
    }

    // Handle pending request responses
    if (hasRequestId(message) && message.requestId && this.pendingRequests.has(message.requestId)) {
      const pendingRequest = this.pendingRequests.get(message.requestId)!;
      this.pendingRequests.delete(message.requestId);

      if (message.type === MessageTypes.ERROR) {
        const errorMsg = (message as DataMessage).data;
        const errorText = (errorMsg && typeof errorMsg === 'object' && 'message' in errorMsg) 
          ? String(errorMsg.message) 
          : 'Request failed';
        pendingRequest.reject(new Error(errorText));
      } else if ('success' in message) {
        const response = message as ResponseMessage;
        if (response.success) {
          pendingRequest.resolve(response.data);
        } else {
          pendingRequest.reject(new Error(response.error || 'Request failed'));
        }
      } else {
        const dataMsg = message as DataMessage;
        pendingRequest.resolve(dataMsg.data);
      }
      return;
    }

    // Handle regular messages
    const handlers = this.messageHandlers.get(message.type);
    if (handlers && handlers.size > 0) {
      try {
        // Execute all handlers
        const promises = Array.from(handlers).map(handler => {
          try {
            return Promise.resolve(handler(message));
          } catch (error) {
            console.error('[MessageBridge] Handler error:', error);
            return Promise.resolve();
          }
        });

        await Promise.all(promises);
      } catch (error) {
        console.error('[MessageBridge] Error handling message:', error);
        
        // Send error response if this was a request
        if (hasRequestId(message)) {
          const errorResponse = createErrorMessage(
            this.source,
            error instanceof Error ? error : new Error(String(error)),
            { originalType: message.type },
            message.requestId
          );
          this.postMessage(errorResponse);
        }
      }
    } else if (this.config.debug) {
      console.warn('[MessageBridge] No handlers for message type:', message.type);
    }
    } finally {
      // Clean up processing flag
      this.messageHandlingInProgress.delete(fingerprint);
    }
  }

  /**
   * Start periodic cleanup of old processed messages
   */
  private startPeriodicCleanup(): void {
    // Run cleanup every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.performPeriodicCleanup();
    }, 5 * 60 * 1000);
  }

  /**
   * Perform periodic cleanup of old processed messages
   */
  private performPeriodicCleanup(): void {
    if (this.isDestroyed) return;

    const cutoff = Date.now() - (this.messageDedupeWindow * MESSAGE_CLEANUP_CUTOFF_MULTIPLIER);
    let cleanedCount = 0;

    this.processedMessages.forEach(fp => {
      const time = parseInt(fp.split('-').pop() || '0') * this.messageDedupeWindow;
      if (time < cutoff) {
        this.processedMessages.delete(fp);
        cleanedCount++;
      }
    });

    if (cleanedCount > 0 && this.config.debug) {
      this.logger.debug(`Periodic cleanup removed ${cleanedCount} old message fingerprints`);
    }
  }

  /**
   * Post message to target window
   */
  private postMessage(message: OrchestratorMessage): void {
    if (!this.targetWindow) {
      this.logger.error('No target window set for message posting');
      return;
    }

    try {
      if (this.config.debug) {
        logMessage('sent', message, true);
      }

      this.targetWindow.postMessage(message, '*');
    } catch (error) {
      this.logger.errorWithContext('Failed to post message', error, { messageType: message.type });
    }
  }
}